import { useState, useCallback } from 'react';
import { Chat } from '../types/chat';
import { useSocketContext } from '../context/SocketContext';
import { chatAPI, systemChatAPI, groupChatAPI, GroupChat } from '../services/api';

export const useChats = () => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [groupChats, setGroupChats] = useState<GroupChat[]>([]);
  const [currentChat, setCurrentChat] = useState<Chat | GroupChat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socket = useSocketContext();

  const loadAndAddSystemChat = useCallback(async () => {
    try {
      const response = await systemChatAPI.getSystemChat();
      const systemChat: Chat = {
        id: 0,
        companionNumber: 'Системный чат',
        companionId: 0,
        unreadCount: 0,
        isNew: false,
        createdAt: new Date().toISOString(),
        messages: response.data.messages || [],
        isGroupChat: false,
      };

      setChats((prev) => {
        const filtered = prev.filter((c) => c.id !== 0);
        return [systemChat, ...filtered];
      });

      socket.joinSystemChat?.();
    } catch (error) {
      console.error('Ошибка загрузки системного чата:', error);
    }
  }, [socket]);

  const loadChats = useCallback(async () => {
    try {
      setIsLoading(true);

      setChats([]);
      setGroupChats([]);

      await loadAndAddSystemChat();

      const [chatResponse, groupResponse] = await Promise.all([chatAPI.getMyChats(), groupChatAPI.getMyGroups()]);

      setChats((prev: Chat[]) => {
        const systemChat = prev.find((c) => c.id === 0);
        const regularChats = chatResponse.data;

        const groupsAsChats = groupResponse.data.map((group: GroupChat) => ({
          id: group.id,
          companionNumber: group.name ? `${group.name} [${group.groupCode}]` : `[${group.groupCode}]`,
          companionId: 0,
          unreadCount: group.unreadCount || 0,
          isNew: group.isNew || false,
          createdAt: group.createdAt,
          messages: group.messages,
          lastMessage: group.lastMessage
            ? {
                preview: group.lastMessage.content,
                time: group.lastMessage.timestamp,
                senderNumber: group.lastMessage.senderNumber,
                isOwn: group.lastMessage.senderNumber === socket.getCurrentUser()?.userNumber,
              }
            : undefined,
          isGroupChat: true,
          groupCode: group.groupCode,
        }));

        return systemChat ? [systemChat, ...regularChats, ...groupsAsChats] : [...regularChats, ...groupsAsChats];
      });

      setGroupChats(groupResponse.data);

      chatResponse.data.forEach((chat: Chat) => {
        if (chat.id !== 0) {
          socket.joinChat(chat.id);
        }
      });

      groupResponse.data.forEach((group: GroupChat) => {
        socket.joinGroup(group.inviteCode);
      });

      console.log('Все чаты загружены успешно');
    } catch (error: any) {
      console.error('Ошибка loadChats:', error);
      setError(error.response?.data?.message || 'Ошибка загрузки чатов');
    } finally {
      setIsLoading(false);
    }
  }, [socket, loadAndAddSystemChat]);

  const loadSystemChat = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await systemChatAPI.getSystemChat();
      const systemChat: Chat = {
        id: 0,
        companionNumber: 'Системный чат',
        companionId: 0,
        unreadCount: 0,
        isNew: false,
        createdAt: new Date().toISOString(),
        messages: response.data.messages || [],
        isGroupChat: false,
      };
      setCurrentChat(systemChat);
      socket.joinSystemChat?.();
    } catch (error: any) {
      console.error('loadSystemChat error:', error);
      setError(error.response?.data?.message || 'Ошибка загрузки системного чата');
    } finally {
      setIsLoading(false);
    }
  }, [socket]);

  const loadChat = useCallback(
    async (chatId: number, isGroupChat: boolean = false) => {
      try {
        setIsLoading(true);
        if (isGroupChat) {
          const response = await groupChatAPI.getGroupWithMessages(chatId);
          const groupChat = response.data;
          const chat: Chat = {
            id: groupChat.id,
            companionNumber: groupChat.name || 'Безымянная группа',
            companionId: 0,
            unreadCount: groupChat.unreadCount || 0,
            isNew: groupChat.isNew || false,
            createdAt: groupChat.createdAt,
            messages: groupChat.messages,
            lastMessage: groupChat.lastMessage
              ? {
                  preview: groupChat.lastMessage.content,
                  time: groupChat.lastMessage.timestamp,
                  senderNumber: groupChat.lastMessage.senderNumber,
                  isOwn: groupChat.lastMessage.senderNumber === socket.getCurrentUser()?.userNumber,
                }
              : undefined,
            isGroupChat: true,
          };
          setCurrentChat(chat);
          socket.joinGroup(groupChat.inviteCode);
        } else {
          const response = await chatAPI.getChatWithMessages(chatId);
          setCurrentChat(response.data);
          socket.joinChat(chatId);
        }
        console.log('currentChat set to:', isGroupChat ? 'групповой' : 'обычный', 'чат', chatId);
      } catch (error: any) {
        console.error('loadChat error:', error);
        setError(error.response?.data?.message || 'Ошибка загрузки чата');
      } finally {
        setIsLoading(false);
      }
    },
    [socket],
  );

  const updateCurrentChat = useCallback(
    (updatedChat: Chat | GroupChat) => {
      if ('inviteCode' in updatedChat) {
        const chat: Chat = {
          id: updatedChat.id,
          companionNumber: updatedChat.name || 'Безымянная группа',
          companionId: 0,
          unreadCount: updatedChat.unreadCount || 0,
          isNew: updatedChat.isNew || false,
          createdAt: updatedChat.createdAt,
          messages: updatedChat.messages,
          lastMessage: updatedChat.lastMessage
            ? {
                preview: updatedChat.lastMessage.content,
                time: updatedChat.lastMessage.timestamp,
                senderNumber: updatedChat.lastMessage.senderNumber,
                isOwn: updatedChat.lastMessage.senderNumber === socket.getCurrentUser()?.userNumber,
              }
            : undefined,
          isGroupChat: true,
        };
        setCurrentChat(chat);
      } else {
        setCurrentChat(updatedChat);
      }
    },
    [socket],
  );

  const sendMessage = useCallback(
    (chatId: number, content: string, tempId?: string, isGroupChat: boolean = false) => {
      if (isGroupChat) {
        socket.sendGroupMessage(chatId, content, tempId);
      } else {
        socket.sendMessage(chatId, content, tempId);
      }
    },
    [socket],
  );

  const deleteChat = useCallback(
    async (chatId: number, isGroupChat: boolean = false) => {
      try {
        if (isGroupChat) {
          await socket.leaveGroup(chatId);
          setGroupChats((prev) => prev.filter((group) => group.id !== chatId));
          setChats((prev) => prev.filter((chat) => chat.id !== chatId || !chat.isGroupChat));
        } else if (socket.getConnectionStatus()) {
          socket.deleteChat(chatId);
        } else {
          await chatAPI.deleteChat(chatId);
          setChats((prev) => prev.filter((chat) => chat.id !== chatId));
        }
        if (currentChat?.id === chatId) {
          setCurrentChat(null);
        }
      } catch (error: any) {
        setError(error.response?.data?.message || 'Ошибка удаления чата');
      }
    },
    [socket, currentChat],
  );

  const clearChat = useCallback(
    async (chatId: number) => {
      try {
        if (socket.getConnectionStatus()) {
          socket.clearChat(chatId);
        } else {
          await chatAPI.clearChat(chatId);
          if (currentChat?.id === chatId) {
            await loadChat(chatId);
          }
        }
      } catch (error: any) {
        setError(error.response?.data?.message || 'Ошибка очистки чата');
      }
    },
    [socket, currentChat, loadChat],
  );

  const createChatByNumber = useCallback(
    async (targetUserNumber: string) => {
      try {
        console.log('Создание чата с', targetUserNumber);
        if (socket.getConnectionStatus()) {
          socket.createChatByNumber(targetUserNumber);
          setTimeout(() => {
            console.log('Принудительная перезагрузка чатов через таймаут');
            loadChats();
          }, 2000);
        } else {
          console.warn('Сокет не подключён, загружаем чаты');
          await loadChats();
        }
      } catch (error: any) {
        console.error('Ошибка создания чата:', error);
        setError(error.response?.data?.message || 'Ошибка создания чата');
      }
    },
    [socket, loadChats],
  );

  const markChatAsRead = useCallback(
    (chatId: number, isGroupChat: boolean = false) => {
      if (!isGroupChat) {
        setChats((prev) => prev.map((chat) => (chat.id === chatId ? { ...chat, unreadCount: 0 } : chat)));
        if (socket.getConnectionStatus()) {
          socket.markAsRead(chatId);
        }
      }
    },
    [socket],
  );

  const createGroupChat = useCallback(
    async (name?: string | null) => {
      try {
        setIsLoading(true);
        const response = await groupChatAPI.createGroup('', 1000);
        const newGroup = response.data;
        setGroupChats((prev) => [...prev, newGroup]);
        const newChat: Chat = {
          id: newGroup.id,
          companionNumber: newGroup.name ? `${newGroup.name} [${newGroup.groupCode}]` : `[${newGroup.groupCode}]`,
          companionId: 0,
          unreadCount: newGroup.unreadCount || 0,
          isNew: newGroup.isNew || true,
          createdAt: newGroup.createdAt,
          messages: newGroup.messages,
          lastMessage: newGroup.lastMessage
            ? {
                preview: newGroup.lastMessage.content,
                time: newGroup.lastMessage.timestamp,
                senderNumber: newGroup.lastMessage.senderNumber,
                isOwn: newGroup.lastMessage.senderNumber === socket.getCurrentUser()?.userNumber,
              }
            : undefined,
          isGroupChat: true,
          groupCode: newGroup.groupCode,
        };
        setChats((prev) => [newChat, ...prev]);
        socket.joinGroup(newGroup.inviteCode);
        console.log('Группа создана:', newGroup);
        return newGroup; // Return newGroup for use in SearchButtons
      } catch (error: any) {
        console.error('Ошибка создания группы:', error);
        setError(error.response?.data?.message || 'Ошибка создания группы');
      } finally {
        setIsLoading(false);
      }
    },
    [socket],
  );

  const updateGroupChatName = useCallback(
    async (groupId: number, name: string | null) => {
      try {
        setIsLoading(true);
        const response = await groupChatAPI.updateGroupName(groupId, name);
        const updatedGroup = response.data;

        setGroupChats((prev) =>
          prev.map((group) =>
            group.id === groupId ? { ...group, name: updatedGroup.name, groupCode: updatedGroup.groupCode } : group,
          ),
        );

        setChats((prev) =>
          prev.map((chat) =>
            chat.id === groupId && chat.isGroupChat
              ? {
                  ...chat,
                  companionNumber: updatedGroup.name
                    ? `${updatedGroup.name} [${updatedGroup.groupCode}]`
                    : `[${updatedGroup.groupCode}]`,
                  groupCode: updatedGroup.groupCode,
                }
              : chat,
          ),
        );

        if (currentChat?.id === groupId && currentChat.isGroupChat) {
          const updatedChat = {
            ...currentChat,
            companionNumber: updatedGroup.name
              ? `${updatedGroup.name} [${updatedGroup.groupCode}]`
              : `[${updatedGroup.groupCode}]`,
            groupCode: updatedGroup.groupCode,
          };

          updateCurrentChat(updatedChat);
        }

        console.log('Имя группы обновлено:', updatedGroup);
      } catch (error: any) {
        console.error('Ошибка обновления имени группы:', error);
        setError(error.response?.data?.message || 'Ошибка обновления имени группы');
      } finally {
        setIsLoading(false);
      }
    },
    [currentChat, updateCurrentChat],
  );

  const joinGroupChat = useCallback(
    async (inviteCode: string) => {
      try {
        setIsLoading(true);
        const response = await groupChatAPI.joinGroup(inviteCode);
        const newGroup = response.data;
        setGroupChats((prev) => [...prev, newGroup]);
        const newChat: Chat = {
          id: newGroup.id,
          companionNumber: newGroup.name || 'Безымянная группа',
          companionId: 0,
          unreadCount: newGroup.unreadCount || 0,
          isNew: newGroup.isNew || true,
          createdAt: newGroup.createdAt,
          messages: newGroup.messages,
          lastMessage: newGroup.lastMessage
            ? {
                preview: newGroup.lastMessage.content,
                time: newGroup.lastMessage.timestamp,
                senderNumber: newGroup.lastMessage.senderNumber,
                isOwn: newGroup.lastMessage.senderNumber === socket.getCurrentUser()?.userNumber,
              }
            : undefined,
          isGroupChat: true,
        };
        setChats((prev) => [newChat, ...prev]);
        socket.joinGroup(inviteCode);
        console.log('Присоединились к группе:', newGroup);
        return newGroup;
      } catch (error: any) {
        console.error('Ошибка присоединения к группе:', error);
        setError(error.response?.data?.message || 'Ошибка присоединения к группе');
      } finally {
        setIsLoading(false);
      }
    },
    [socket],
  );

  const leaveGroupChat = useCallback(
    async (groupId: number) => {
      try {
        setIsLoading(true);
        await groupChatAPI.leaveGroup(groupId);
        socket.leaveGroup(groupId);
        setGroupChats((prev) => prev.filter((group) => group.id !== groupId));
        setChats((prev) => prev.filter((chat) => chat.id !== groupId || !chat.isGroupChat));
        if (currentChat?.id === groupId) {
          setCurrentChat(null);
        }
        console.log('Покинули группу:', groupId);
      } catch (error: any) {
        console.error('Ошибка выхода из группы:', error);
        setError(error.response?.data?.message || 'Ошибка выхода из группы');
      } finally {
        setIsLoading(false);
      }
    },
    [socket, currentChat],
  );

  return {
    chats,
    groupChats,
    currentChat,
    isLoading,
    error,
    loadChats,
    loadChat,
    sendMessage,
    deleteChat,
    clearChat,
    loadSystemChat,
    updateCurrentChat,
    markChatAsRead,
    createChatByNumber,
    createGroupChat,
    joinGroupChat,
    leaveGroupChat,
    setChats,
    setGroupChats,
    setError,
  };
};
